import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import AccountingShell from "./AccountingShell";
import toast from "react-hot-toast";

const ACCOUNTS_EMAIL = "accounts@shineagri.org";

const INR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const num = (v) => Number(v || 0);

const toDateInput = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : new Date();
    return d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : "";
  } catch {
    return "";
  }
};

const fmtDate = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-IN") : "—";
  } catch {
    return "—";
  }
};

const makeVoucherNo = () => {
  const d = new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 4 ? y : y - 1;
  const fy = `${String(start).slice(-2)}${String(start + 1).slice(-2)}`;
  return `RA-PAY/${fy}/${String(Date.now()).slice(-6)}`;
};

function Badge({ children }) {
  const s = String(children || "").toUpperCase();
  let cls = "bg-amber-100 text-amber-700";
  if (s.includes("FULL") || s.includes("PAID") || s.includes("APPROVED"))
    cls = "bg-emerald-100 text-emerald-700";
  if (s.includes("PART")) cls = "bg-blue-100 text-blue-700";
  if (s.includes("PENDING")) cls = "bg-amber-100 text-amber-700";

  return (
    <span className={`px-3 py-1 rounded-full text-[10px] font-black ${cls}`}>
      {children || "—"}
    </span>
  );
}

export default function RABillPaymentAllocation() {
  return (
    <AccountingShell title="RA Bill Payment Allocation">
      <RABillPaymentAllocationContent />
    </AccountingShell>
  );
}

function RABillPaymentAllocationContent({ me = {} }) {
  const [raBills, setRaBills] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [paymentVouchers, setPaymentVouchers] = useState([]);

  const [selectedVendor, setSelectedVendor] = useState("ALL");
  const [selectedTender, setSelectedTender] = useState("ALL");
  const [search, setSearch] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [mode, setMode] = useState("FIFO");

  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(toDateInput(new Date()));
  const [utr, setUtr] = useState("");
  const [voucherNo, setVoucherNo] = useState(makeVoucherNo());

  const [historyBill, setHistoryBill] = useState(null);
  const [editVoucher, setEditVoucher] = useState(null);
  const [editVoucherNo, setEditVoucherNo] = useState("");
  const [editPaymentDate, setEditPaymentDate] = useState("");
  const [editUtr, setEditUtr] = useState("");
  const [editTotalAmount, setEditTotalAmount] = useState("");
  const [editAllocations, setEditAllocations] = useState({});

  const load = async () => {
    const [raSnap, tenderSnap, voucherSnap] = await Promise.all([
      getDocs(collection(db, "ra_bills")),
      getDocs(collection(db, "tenders")),
      getDocs(collection(db, "ra_payment_vouchers")).catch(() => ({ docs: [] })),
    ]);

    setRaBills(raSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setTenders(tenderSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setPaymentVouchers(voucherSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    load();
  }, []);

  const paymentHistoryMap = useMemo(() => {
    const map = {};

    paymentVouchers.forEach((v) => {
      (v.allocations || []).forEach((a) => {
        if (!a.raBillId) return;
        if (!map[a.raBillId]) map[a.raBillId] = [];

        map[a.raBillId].push({
          voucherId: v.id,
          voucherNo: v.voucherNo,
          paymentDate: v.paymentDate,
          paymentUtr: v.paymentUtr,
          allocationMode: v.allocationMode,
          adjustedAmount: num(a.adjustedAmount),
          outstandingBefore: num(a.outstandingBefore),
          outstandingAfter: num(a.outstandingAfter),
          invoiceAmount: num(a.invoiceAmount),
          status: a.status,
          createdAt: v.createdAt,
        });
      });
    });

    Object.keys(map).forEach((id) => {
      map[id].sort((a, b) => {
        const ad = a.createdAt?.seconds || new Date(a.paymentDate || 0).getTime();
        const bd = b.createdAt?.seconds || new Date(b.paymentDate || 0).getTime();
        return ad - bd;
      });
    });

    return map;
  }, [paymentVouchers]);

  const rows = useMemo(() => {
    return raBills
      .map((r) => {
        const tender = tenders.find((t) => t.id === r.tenderId) || {};
        const invoiceAmount = num(
          r.netPayable || r.net || r.grossWithGST || r.totalAmount || r.billAmount
        );

        const history = paymentHistoryMap[r.id] || [];
        const historyPaid = history.reduce((s, h) => s + num(h.adjustedAmount), 0);

        const paidAmount = Math.max(
          num(r.paymentAmount || r.totalPaidAmount || r.allocatedPaidAmount),
          historyPaid
        );

        const outstanding =
          r.outstandingAmount !== undefined
            ? num(r.outstandingAmount)
            : Math.max(invoiceAmount - paidAmount, 0);

        return {
          ...r,
          contractorName:
            r.vendorName ||
            r.contractorName ||
            tender.awardedVendorName ||
            r.vendorUid ||
            "Unknown",
          contractorEmail:
            r.vendorEmail ||
            r.contractorEmail ||
            tender.awardedVendorEmail ||
            r.email ||
            "",
          pacsName:
            tender.ownerCompany ||
            tender.pacsName ||
            tender.projectName ||
            tender.title ||
            r.projectName ||
            "Unknown Project",
          invoiceNo: r.raInvoiceNo || r.invoiceNo || r.raBillNo || r.billNo || r.id,
          invoiceDate: r.raInvoiceDate || r.invoiceDate || r.raDate || r.createdAt,
          invoiceAmount,
          paidAmount,
          outstanding,
          paymentHistory: history,
          paymentStatus:
            outstanding <= 0 && paidAmount > 0
              ? "FULLY_PAID"
              : paidAmount > 0
              ? "PART_PAID"
              : r.paymentStatus || "PENDING",
        };
      })
      .filter((r) => r.accountingStatus === "CEO_APPROVED")
      .filter((r) => r.outstanding > 0 || r.paymentHistory?.length);
  }, [raBills, tenders, paymentHistoryMap]);

  const vendors = useMemo(() => {
    const set = new Set(rows.map((r) => r.vendorUid || r.contractorName));
    return ["ALL", ...Array.from(set)];
  }, [rows]);

  const tenderList = useMemo(() => {
    const set = new Set(rows.map((r) => r.tenderId).filter(Boolean));
    return ["ALL", ...Array.from(set)];
  }, [rows]);

  const filtered = useMemo(() => {
    let list = [...rows];

    if (selectedVendor !== "ALL") {
      list = list.filter((r) => (r.vendorUid || r.contractorName) === selectedVendor);
    }

    if (selectedTender !== "ALL") {
      list = list.filter((r) => r.tenderId === selectedTender);
    }

    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        [
          r.invoiceNo,
          r.contractorName,
          r.contractorEmail,
          r.pacsName,
          r.tenderId,
          r.vendorUid,
          ...(r.paymentHistory || []).map((h) => `${h.voucherNo} ${h.paymentUtr}`),
        ]
          .join(" ")
          .toLowerCase()
          .includes(s)
      );
    }

    return list.sort(
      (a, b) =>
        new Date(a.invoiceDate || a.createdAt || 0) -
        new Date(b.invoiceDate || b.createdAt || 0)
    );
  }, [rows, selectedVendor, selectedTender, search]);

  const selectedRows = filtered.filter((r) => selectedIds.includes(r.id));

  const totals = useMemo(() => {
    const outstanding = selectedRows.reduce((s, r) => s + num(r.outstanding), 0);
    const allocated = selectedRows.reduce((s, r) => s + num(allocations[r.id]), 0);
    return { outstanding, allocated, remainingPayment: num(paymentAmount) - allocated };
  }, [selectedRows, allocations, paymentAmount]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllVisible = () => {
    setSelectedIds(filtered.filter((r) => r.outstanding > 0).map((r) => r.id));
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setAllocations({});
  };

  const autoAllocate = () => {
    const totalPay = num(paymentAmount);
    if (!totalPay || totalPay <= 0) return toast.error("Payment amount enter karo.");
    if (!selectedRows.length) return toast.error("Invoice select karo.");

    let next = {};
    let remaining = totalPay;

    if (mode === "FIFO") {
      selectedRows.forEach((r) => {
        const adj = Math.min(num(r.outstanding), remaining);
        next[r.id] = adj;
        remaining -= adj;
      });
    }

    if (mode === "PRO_RATA") {
      const totalOutstanding = selectedRows.reduce((s, r) => s + num(r.outstanding), 0);
      selectedRows.forEach((r, idx) => {
        let adj =
          idx === selectedRows.length - 1
            ? remaining
            : Math.round((totalPay * num(r.outstanding)) / totalOutstanding);
        adj = Math.min(adj, num(r.outstanding));
        next[r.id] = adj;
        remaining -= adj;
      });
    }

    setAllocations(next);
    toast.success("Allocation applied.");
  };

  const safeAddLedger = async (payload) => {
    const q = query(
      collection(db, "contractor_ledgers"),
      where("type", "==", payload.type),
      where("voucherNo", "==", payload.voucherNo)
    );

    const snap = await getDocs(q);
    if (!snap.empty) return false;

    await addDoc(collection(db, "contractor_ledgers"), {
      ...payload,
      createdAt: serverTimestamp(),
      createdBy: me.uid || "",
      createdByRole: me.role || "",
    });

    return true;
  };

  const queueMail = async ({ to, subject, body, data }) => {
    if (!to) return;

    await addDoc(collection(db, "mail_queue"), {
      to,
      subject,
      body,
      type: "RA_BILL_BULK_PAYMENT_ALLOCATION",
      status: "PENDING",
      data,
      createdAt: serverTimestamp(),
      createdBy: me.uid || "",
    });
  };

  const submitPayment = async () => {
    if (!["ACCOUNTANT", "ADMIN"].includes(me.role)) {
      toast.error("Only Accountant/Admin allowed.");
      return;
    }

    if (!selectedRows.length) return toast.error("Invoice select karo.");
    if (!utr.trim()) return toast.error("UTR / Reference No compulsory hai.");
    if (!paymentDate) return toast.error("Payment date compulsory hai.");

    const totalPaid = num(paymentAmount);
    if (totalPaid <= 0) return toast.error("Payment amount valid nahi hai.");

    const allocationRows = selectedRows
      .map((r) => {
        const adjusted = num(allocations[r.id]);
        if (adjusted <= 0) return null;

        const oldPaid = num(r.paidAmount);
        const outstandingBefore = num(r.outstanding);
        const outstandingAfter = Math.max(outstandingBefore - adjusted, 0);
        const newPaid = oldPaid + adjusted;

        return {
          raBillId: r.id,
          invoiceNo: r.invoiceNo,
          invoiceDate: r.invoiceDate || "",
          invoiceAmount: r.invoiceAmount,
          alreadyPaid: oldPaid,
          outstandingBefore,
          adjustedAmount: adjusted,
          outstandingAfter,
          totalPaidAfter: newPaid,
          status: outstandingAfter <= 0 ? "FULLY_PAID" : "PART_PAID",
          contractorEmail: r.contractorEmail,
          contractorName: r.contractorName,
          tenderId: r.tenderId,
          vendorUid: r.vendorUid || "",
          pacsName: r.pacsName,
        };
      })
      .filter(Boolean);

    const allocatedTotal = allocationRows.reduce((s, r) => s + r.adjustedAmount, 0);

    if (allocatedTotal <= 0) return toast.error("Allocation amount zero hai.");
    if (allocatedTotal > totalPaid)
      return toast.error("Allocation payment amount se jyada hai.");

    const ok = window.confirm(
      `Payment Voucher ${voucherNo}\nTotal Payment: ${INR(totalPaid)}\nAllocated: ${INR(
        allocatedTotal
      )}\nConfirm?`
    );
    if (!ok) return;

    const voucherRef = await addDoc(collection(db, "ra_payment_vouchers"), {
      voucherNo,
      paymentDate,
      paymentUtr: utr,
      totalPaymentAmount: totalPaid,
      allocatedAmount: allocatedTotal,
      unallocatedAmount: totalPaid - allocatedTotal,
      allocationMode: mode,
      allocations: allocationRows,
      status: "POSTED",
      createdAt: serverTimestamp(),
      createdBy: me.uid || "",
      createdByRole: me.role || "",
    });

    for (const a of allocationRows) {
      await updateDoc(doc(db, "ra_bills", a.raBillId), {
        paymentStatus: a.status,
        totalPaidAmount: a.totalPaidAfter,
        allocatedPaidAmount: a.totalPaidAfter,
        outstandingAmount: a.outstandingAfter,
        lastPaymentVoucherNo: voucherNo,
        lastPaymentVoucherId: voucherRef.id,
        lastPaymentUtr: utr,
        lastPaymentDate: paymentDate,
        paymentUpdatedBy: me.uid || "",
        paymentUpdatedAt: serverTimestamp(),
      });
    }

    await safeAddLedger({
      type: "RA_BILL_BULK_PAYMENT",
      voucherNo,
      referenceNo: utr,
      paymentUtr: utr,
      paymentDate,
      debit: 0,
      credit: allocatedTotal,
      grossAmount: allocatedTotal,
      serviceName: "RA Bill Bulk Payment",
      narration: `RA bill bulk payment adjusted against ${allocationRows.length} invoice(s). UTR: ${utr}`,
      allocations: allocationRows,
      status: "POSTED",
    });

    await sendPaymentMail(voucherNo, paymentDate, utr, allocationRows);

    toast.success("Payment allocated, ledger posted, mail queued.");
    setSelectedIds([]);
    setAllocations({});
    setPaymentAmount("");
    setUtr("");
    setVoucherNo(makeVoucherNo());
    load();
  };

  const sendPaymentMail = async (vNo, pDate, pUtr, allocationRows) => {
    const partyGroups = {};

    allocationRows.forEach((a) => {
      const key = a.contractorEmail || a.vendorUid || a.contractorName;
      if (!partyGroups[key]) partyGroups[key] = [];
      partyGroups[key].push(a);
    });

    for (const key of Object.keys(partyGroups)) {
      const group = partyGroups[key];
      const contractorEmail = group[0].contractorEmail;
      const contractorName = group[0].contractorName;
      const partyTotal = group.reduce((s, x) => s + x.adjustedAmount, 0);

      const body = `
Dear ${contractorName},

RA Bill payment has been processed / updated.

Voucher No: ${vNo}
Payment Date: ${fmtDate(pDate)}
UTR / Reference: ${pUtr}
Total Adjusted Amount: ${INR(partyTotal)}

Invoice-wise adjustment:
${group
  .map(
    (x, i) =>
      `${i + 1}. Invoice ${x.invoiceNo} | Invoice Amount: ${INR(
        x.invoiceAmount
      )} | Outstanding Before: ${INR(x.outstandingBefore)} | Paid: ${INR(
        x.adjustedAmount
      )} | Balance: ${INR(x.outstandingAfter)} | Status: ${x.status}`
  )
  .join("\n")}

Regards,
Accounts Department
Shine Agri Allied & Marketing MSCS Ltd.
      `;

      await queueMail({
        to: [ACCOUNTS_EMAIL, contractorEmail].filter(Boolean).join(","),
        subject: `RA Bill Payment Adjustment - ${vNo}`,
        body,
        data: { voucherNo: vNo, paymentDate: pDate, paymentUtr: pUtr, allocations: group },
      });
    }
  };

  const openEditVoucher = (historyRow) => {
    const v = paymentVouchers.find((x) => x.id === historyRow.voucherId);
    if (!v) return toast.error("Voucher not found.");

    setEditVoucher(v);
    setEditVoucherNo(v.voucherNo || "");
    setEditPaymentDate(toDateInput(v.paymentDate));
    setEditUtr(v.paymentUtr || "");
    setEditTotalAmount(String(v.totalPaymentAmount || 0));

    const map = {};
    (v.allocations || []).forEach((a) => {
      map[a.raBillId] = num(a.adjustedAmount);
    });
    setEditAllocations(map);
  };

  const recalcRaBillAfterVoucherEdit = async (raBillId, editedVoucherId, newAllocations) => {
    const bill = raBills.find((r) => r.id === raBillId);
    if (!bill) return;

    const invoiceAmount = num(
      bill.netPayable || bill.net || bill.grossWithGST || bill.totalAmount || bill.billAmount
    );

    let paid = 0;
    let lastVoucherNo = "";
    let lastUtr = "";
    let lastDate = "";

    paymentVouchers.forEach((v) => {
      if (v.id === editedVoucherId) return;

      (v.allocations || []).forEach((a) => {
        if (a.raBillId === raBillId) {
          paid += num(a.adjustedAmount);
          lastVoucherNo = v.voucherNo || lastVoucherNo;
          lastUtr = v.paymentUtr || lastUtr;
          lastDate = v.paymentDate || lastDate;
        }
      });
    });

    newAllocations.forEach((a) => {
      if (a.raBillId === raBillId) {
        paid += num(a.adjustedAmount);
        lastVoucherNo = editVoucherNo;
        lastUtr = editUtr;
        lastDate = editPaymentDate;
      }
    });

    const outstanding = Math.max(invoiceAmount - paid, 0);
    const status =
      paid > 0 && outstanding <= 0 ? "FULLY_PAID" : paid > 0 ? "PART_PAID" : "PENDING";

    await updateDoc(doc(db, "ra_bills", raBillId), {
      paymentStatus: status,
      totalPaidAmount: paid,
      allocatedPaidAmount: paid,
      outstandingAmount: outstanding,
      lastPaymentVoucherNo: lastVoucherNo,
      lastPaymentUtr: lastUtr,
      lastPaymentDate: lastDate,
      paymentUpdatedAt: serverTimestamp(),
      paymentEditedAt: serverTimestamp(),
      paymentEditedBy: me.uid || "",
    });
  };

  const saveVoucherEdit = async () => {
    if (!["ACCOUNTANT", "ADMIN"].includes(me.role)) {
      toast.error("Only Accountant/Admin allowed.");
      return;
    }

    if (!editVoucher) return;
    if (!editVoucherNo.trim()) return toast.error("Voucher No required.");
    if (!editPaymentDate) return toast.error("Payment date required.");
    if (!editUtr.trim()) return toast.error("UTR required.");

    const totalAmount = num(editTotalAmount);
    if (totalAmount <= 0) return toast.error("Total amount invalid.");

    const oldAllocations = editVoucher.allocations || [];

    const newAllocations = oldAllocations.map((a) => {
      const invoiceAmount = num(a.invoiceAmount);
      const adjustedAmount = Math.min(num(editAllocations[a.raBillId]), invoiceAmount);
      const outstandingBefore = num(a.outstandingBefore);
      const outstandingAfter = Math.max(outstandingBefore - adjustedAmount, 0);

      return {
        ...a,
        adjustedAmount,
        outstandingAfter,
        totalPaidAfter: num(a.alreadyPaid) + adjustedAmount,
        status:
          outstandingAfter <= 0
            ? "FULLY_PAID"
            : adjustedAmount > 0
            ? "PART_PAID"
            : "PENDING",
      };
    });

    const allocatedAmount = newAllocations.reduce((s, a) => s + num(a.adjustedAmount), 0);

    if (allocatedAmount > totalAmount) {
      toast.error("Allocated amount total payment se jyada hai.");
      return;
    }

    const oldVoucherNo = editVoucher.voucherNo;

    await updateDoc(doc(db, "ra_payment_vouchers", editVoucher.id), {
      voucherNo: editVoucherNo,
      paymentDate: editPaymentDate,
      paymentUtr: editUtr,
      totalPaymentAmount: totalAmount,
      allocatedAmount,
      unallocatedAmount: totalAmount - allocatedAmount,
      allocations: newAllocations,
      editedAt: serverTimestamp(),
      editedBy: me.uid || "",
      editRemark: "Payment voucher corrected by accountant",
    });

    const affectedIds = [...new Set(oldAllocations.map((a) => a.raBillId).filter(Boolean))];

    for (const id of affectedIds) {
      await recalcRaBillAfterVoucherEdit(id, editVoucher.id, newAllocations);
    }

    const ledgerSnap = await getDocs(
      query(
        collection(db, "contractor_ledgers"),
        where("type", "==", "RA_BILL_BULK_PAYMENT"),
        where("voucherNo", "==", oldVoucherNo)
      )
    );

    for (const d of ledgerSnap.docs) {
      await updateDoc(doc(db, "contractor_ledgers", d.id), {
        voucherNo: editVoucherNo,
        referenceNo: editUtr,
        paymentUtr: editUtr,
        paymentDate: editPaymentDate,
        credit: allocatedAmount,
        grossAmount: allocatedAmount,
        allocations: newAllocations,
        narration: `RA bill bulk payment corrected and adjusted against ${newAllocations.length} invoice(s). UTR: ${editUtr}`,
        editedAt: serverTimestamp(),
        editedBy: me.uid || "",
      });
    }

    await sendPaymentMail(editVoucherNo, editPaymentDate, editUtr, newAllocations);

    toast.success("Voucher No, Date, UTR, Amount, RA bills and Ledger updated.");
    setEditVoucher(null);
    setHistoryBill(null);
    load();
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
        <Card title="Outstanding Invoices" value={filtered.filter((x) => x.outstanding > 0).length} />
        <Card title="Selected Invoices" value={selectedRows.length} />
        <Card title="Selected Outstanding" value={INR(totals.outstanding)} red />
        <Card title="Allocated" value={INR(totals.allocated)} green />
        <Card title="Unallocated Payment" value={INR(totals.remainingPayment)} />
      </div>

      <div className="bg-white rounded-2xl border p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select className="input" value={selectedVendor} onChange={(e) => setSelectedVendor(e.target.value)}>
            {vendors.map((v) => (
              <option key={v} value={v}>{v === "ALL" ? "All Contractors" : v}</option>
            ))}
          </select>

          <select className="input" value={selectedTender} onChange={(e) => setSelectedTender(e.target.value)}>
            {tenderList.map((t) => (
              <option key={t} value={t}>{t === "ALL" ? "All Tenders" : t}</option>
            ))}
          </select>

          <input
            className="input"
            placeholder="Search invoice / contractor / UTR / voucher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <button className="btn-dark" onClick={selectAllVisible}>Select All Visible</button>
          <button className="btn-gray" onClick={clearSelection}>Clear</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border p-4">
        <h2 className="font-black text-lg mb-3">Payment Entry</h2>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <input className="input" value={voucherNo} onChange={(e) => setVoucherNo(e.target.value)} placeholder="Voucher No" />
          <input className="input" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
          <input className="input" value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="UTR / Ref No" />
          <input className="input" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder="Payment Amount" />
          <select className="input" value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="FIFO">FIFO Auto Adjust</option>
            <option value="PRO_RATA">Pro-rata Adjust</option>
            <option value="MANUAL">Manual Allocation</option>
          </select>
          <button className="btn" onClick={autoAllocate}>Auto Allocate</button>
        </div>

        <div className="mt-3 flex justify-end">
          <button className="btn-dark" onClick={submitPayment}>
            Submit Payment + Ledger + Mail
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border p-4">
        <h2 className="font-black text-center text-lg mb-1">RA Bill Outstanding Allocation Register</h2>
        <p className="text-xs text-slate-500 text-center mb-4">
          Invoice wise previous payment history + current allocation detail.
        </p>

        <div className="overflow-auto">
          <table className="min-w-[2100px] w-full text-[11px] border border-slate-300">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="p-2 border text-center">Select</th>
                <th className="p-2 border text-left">Invoice</th>
                <th className="p-2 border text-left">Contractor</th>
                <th className="p-2 border text-left">Project</th>
                <th className="p-2 border text-right">Invoice Amount</th>
                <th className="p-2 border text-right">Paid Earlier</th>
                <th className="p-2 border text-left">Payment History</th>
                <th className="p-2 border text-right">Outstanding</th>
                <th className="p-2 border text-right">Allocate Payment</th>
                <th className="p-2 border text-right">Balance After</th>
                <th className="p-2 border text-center">Status</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((r) => {
                const checked = selectedIds.includes(r.id);
                const allocated = num(allocations[r.id]);
                const balanceAfter = Math.max(num(r.outstanding) - allocated, 0);

                return (
                  <tr key={r.id} className={checked ? "bg-emerald-50" : "hover:bg-slate-50"}>
                    <td className="p-2 border text-center">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={r.outstanding <= 0}
                        onChange={() => toggleSelect(r.id)}
                      />
                    </td>

                    <td className="p-2 border font-black">
                      {r.invoiceNo}
                      <div className="text-[10px] text-slate-500">{fmtDate(r.invoiceDate)}</div>
                      <div className="text-[10px] text-slate-400">ID: {r.id}</div>
                    </td>

                    <td className="p-2 border font-bold">
                      {r.contractorName}
                      <div className="text-[10px] text-slate-500">{r.contractorEmail || "No email"}</div>
                    </td>

                    <td className="p-2 border">
                      {r.pacsName}
                      <div className="text-[10px] text-slate-500">Tender: {r.tenderId || "—"}</div>
                    </td>

                    <td className="p-2 border text-right font-black">{INR(r.invoiceAmount)}</td>
                    <td className="p-2 border text-right text-emerald-700 font-black">{INR(r.paidAmount)}</td>

                    <td className="p-2 border">
                      {r.paymentHistory?.length ? (
                        <div className="space-y-1">
                          {r.paymentHistory.slice(-3).map((h, idx) => (
                            <div key={idx} className="rounded-lg border bg-slate-50 p-2">
                              <div className="font-black">{h.voucherNo}</div>
                              <div className="text-[10px] text-slate-500">
                                {fmtDate(h.paymentDate)} | UTR: {h.paymentUtr || "—"}
                              </div>
                              <div className="text-[10px]">
                                Paid: <b className="text-emerald-700">{INR(h.adjustedAmount)}</b> | Bal:{" "}
                                <b className="text-red-600">{INR(h.outstandingAfter)}</b>
                              </div>
                            </div>
                          ))}
                          <button className="btn-mini" onClick={() => setHistoryBill(r)}>
                            Full History
                          </button>
                        </div>
                      ) : (
                        <span className="text-slate-400">No previous payment</span>
                      )}
                    </td>

                    <td className="p-2 border text-right text-red-600 font-black">{INR(r.outstanding)}</td>

                    <td className="p-2 border text-right">
                      <input
                        className="input text-right"
                        disabled={r.outstanding <= 0}
                        value={allocations[r.id] || ""}
                        onChange={(e) => {
                          const val = Math.min(num(e.target.value), num(r.outstanding));
                          setAllocations((p) => ({ ...p, [r.id]: val }));
                          if (!selectedIds.includes(r.id)) setSelectedIds((p) => [...p, r.id]);
                        }}
                        placeholder="0"
                      />
                    </td>

                    <td className="p-2 border text-right font-black">{INR(balanceAfter)}</td>

                    <td className="p-2 border text-center">
                      <Badge>
                        {balanceAfter <= 0 && allocated > 0
                          ? "FULLY_PAID"
                          : allocated > 0
                          ? "PART_PAID"
                          : r.paymentStatus}
                      </Badge>
                    </td>
                  </tr>
                );
              })}

              {!filtered.length && (
                <tr>
                  <td colSpan={11} className="p-8 text-center text-slate-500">
                    No RA bill invoices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {historyBill && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full shadow-2xl overflow-hidden">
            <div className="bg-slate-900 text-white p-5 flex justify-between">
              <div>
                <h2 className="font-black text-lg">Payment History - {historyBill.invoiceNo}</h2>
                <p className="text-xs text-slate-300">{historyBill.contractorName}</p>
              </div>
              <button className="bg-white text-slate-900 rounded-lg px-3 py-1 font-black" onClick={() => setHistoryBill(null)}>
                X
              </button>
            </div>

            <div className="p-5 overflow-auto">
              <table className="min-w-[1050px] w-full text-xs border">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-3 border">Sr</th>
                    <th className="p-3 border text-left">Voucher</th>
                    <th className="p-3 border text-center">Date</th>
                    <th className="p-3 border text-left">UTR</th>
                    <th className="p-3 border text-right">Outstanding Before</th>
                    <th className="p-3 border text-right">Paid</th>
                    <th className="p-3 border text-right">Balance After</th>
                    <th className="p-3 border text-center">Status</th>
                    <th className="p-3 border text-center">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {(historyBill.paymentHistory || []).map((h, i) => (
                    <tr key={i}>
                      <td className="p-3 border text-center">{i + 1}</td>
                      <td className="p-3 border font-black">{h.voucherNo}</td>
                      <td className="p-3 border text-center">{fmtDate(h.paymentDate)}</td>
                      <td className="p-3 border">{h.paymentUtr || "—"}</td>
                      <td className="p-3 border text-right">{INR(h.outstandingBefore)}</td>
                      <td className="p-3 border text-right text-emerald-700 font-black">{INR(h.adjustedAmount)}</td>
                      <td className="p-3 border text-right text-red-600 font-black">{INR(h.outstandingAfter)}</td>
                      <td className="p-3 border text-center"><Badge>{h.status}</Badge></td>
                      <td className="p-3 border text-center">
                        <button className="btn-gray" onClick={() => openEditVoucher(h)}>
                          Edit Voucher
                        </button>
                      </td>
                    </tr>
                  ))}

                  {!historyBill.paymentHistory?.length && (
                    <tr>
                      <td colSpan={9} className="p-8 text-center text-slate-500">
                        No payment history found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {editVoucher && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full shadow-2xl overflow-hidden">
            <div className="bg-slate-900 text-white p-5 flex justify-between">
              <div>
                <h2 className="font-black text-lg">Edit Payment Voucher</h2>
                <p className="text-xs text-slate-300">
                  Voucher No, Date, UTR, Total Amount and invoice-wise allocation edit
                </p>
              </div>
              <button
                className="bg-white text-slate-900 rounded-lg px-3 py-1 font-black"
                onClick={() => setEditVoucher(null)}
              >
                X
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-black text-slate-500">Voucher No</label>
                  <input className="input" value={editVoucherNo} onChange={(e) => setEditVoucherNo(e.target.value)} />
                </div>

                <div>
                  <label className="text-xs font-black text-slate-500">Payment Date</label>
                  <input className="input" type="date" value={editPaymentDate} onChange={(e) => setEditPaymentDate(e.target.value)} />
                </div>

                <div>
                  <label className="text-xs font-black text-slate-500">UTR / Ref No</label>
                  <input className="input" value={editUtr} onChange={(e) => setEditUtr(e.target.value)} />
                </div>

                <div>
                  <label className="text-xs font-black text-slate-500">Total Payment Amount</label>
                  <input className="input" value={editTotalAmount} onChange={(e) => setEditTotalAmount(e.target.value)} />
                </div>
              </div>

              <div className="overflow-auto border rounded-xl">
                <table className="min-w-[1000px] w-full text-xs">
                  <thead className="bg-slate-900 text-white">
                    <tr>
                      <th className="p-3 border text-left">Invoice</th>
                      <th className="p-3 border text-left">Contractor</th>
                      <th className="p-3 border text-right">Invoice Amount</th>
                      <th className="p-3 border text-right">Outstanding Before</th>
                      <th className="p-3 border text-right">Paid / Adjust</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(editVoucher.allocations || []).map((a) => (
                      <tr key={a.raBillId}>
                        <td className="p-3 border font-black">{a.invoiceNo}</td>
                        <td className="p-3 border">{a.contractorName || "—"}</td>
                        <td className="p-3 border text-right">{INR(a.invoiceAmount)}</td>
                        <td className="p-3 border text-right">{INR(a.outstandingBefore)}</td>
                        <td className="p-3 border text-right">
                          <input
                            className="input text-right"
                            value={editAllocations[a.raBillId] || ""}
                            onChange={(e) =>
                              setEditAllocations((p) => ({
                                ...p,
                                [a.raBillId]: num(e.target.value),
                              }))
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2">
                <button className="btn-gray" onClick={() => setEditVoucher(null)}>
                  Cancel
                </button>
                <button className="btn-dark" onClick={saveVoucherEdit}>
                  Save Voucher Correction
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input{border:1px solid #cbd5e1;border-radius:12px;padding:9px 12px;font-size:12px;font-weight:800;width:100%}
        .btn{background:#059669;color:white;padding:9px 12px;border-radius:12px;font-size:12px;font-weight:900}
        .btn-dark{background:#0f172a;color:white;padding:9px 12px;border-radius:12px;font-size:12px;font-weight:900}
        .btn-gray{background:#e2e8f0;color:#0f172a;padding:9px 12px;border-radius:12px;font-size:12px;font-weight:900}
        .btn-mini{background:#0f172a;color:white;padding:5px 9px;border-radius:8px;font-size:10px;font-weight:900}
      `}</style>
    </div>
  );
}

function Card({ title, value, green, red }) {
  return (
    <div className="bg-white rounded-2xl border p-4 shadow-sm">
      <p className="text-xs text-slate-500">{title}</p>
      <h2
        className={`text-lg font-black mt-2 ${
          green ? "text-emerald-700" : red ? "text-red-600" : "text-slate-900"
        }`}
      >
        {value}
      </h2>
    </div>
  );
}